import { useState, FormEvent, useEffect } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useNavigate, Link } from 'react-router-dom'
import { tenants, projects, tenantDomains } from '@/api/client'
import { ArrowLeft, Loader2, Info, RefreshCw, CheckCircle2, AlertTriangle } from 'lucide-react'

export function NewProjectPage() {
  const navigate = useNavigate()
  const tenantsQuery = useQuery({ queryKey: ['tenants'], queryFn: () => tenants.list() })

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [sourceTenantId, setSourceTenantId] = useState('')
  const [destinationTenantId, setDestinationTenantId] = useState('')
  const [selectedDomains, setSelectedDomains] = useState<string[]>([])
  const [refreshKey, setRefreshKey] = useState(0)

  // Carrega domínios do tenant origem (Discovery raso)
  const sourceDomainsQuery = useQuery({
    queryKey: ['tenant-domains', sourceTenantId, refreshKey],
    queryFn: () => tenantDomains.summary(sourceTenantId, refreshKey > 0),
    enabled: !!sourceTenantId,
  })

  // Limpa seleção quando muda tenant origem
  useEffect(() => {
    setSelectedDomains([])
  }, [sourceTenantId])

  const create = useMutation({
    mutationFn: () => {
      if (selectedDomains.length === 0) {
        throw new Error('Selecione pelo menos 1 domínio')
      }
      return projects.create({
        name,
        description: description || undefined,
        sourceTenantId,
        destinationTenantId,
        domain: selectedDomains[0],            // legado, mantido pra compat
        domains: selectedDomains,                // multi-domain Sprint 3
        scopeType: 'WholeDomain',
      })
    },
    onSuccess: (project) => {
      navigate(`/projects/${project.id}`)
    },
  })

  const activeTenants = tenantsQuery.data?.filter(t => t.status === 2) || []

  function toggleDomain(name: string) {
    setSelectedDomains(prev =>
      prev.includes(name) ? prev.filter(d => d !== name) : [...prev, name]
    )
  }

  function selectAll() {
    if (sourceDomainsQuery.data) {
      setSelectedDomains(sourceDomainsQuery.data.domains.filter(d => d.userCount > 0).map(d => d.name))
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    create.mutate()
  }

  return (
    <div className="max-w-3xl">
      <Link to="/projects" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4">
        <ArrowLeft className="h-4 w-4" /> Voltar
      </Link>

      <h1 className="text-2xl font-semibold text-slate-900 mb-1">Novo projeto de migração</h1>
      <p className="text-sm text-slate-500 mb-6">
        Selecione os domínios origem que entrarão no projeto. O planejamento detalhado
        (mapeamento de UPN, licenças, conteúdo) acontece depois do Discovery.
      </p>

      <form onSubmit={onSubmit} className="card p-6 space-y-5">
        <div>
          <label className="label">Nome do projeto *</label>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex: Migração AMOREE → ALARAV maio 2026"
            required
            autoFocus
          />
        </div>

        <div>
          <label className="label">Descrição (opcional)</label>
          <textarea
            className="input min-h-[60px] resize-y"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label">Tenant origem *</label>
            <select
              className="input"
              value={sourceTenantId}
              onChange={(e) => setSourceTenantId(e.target.value)}
              required
            >
              <option value="">Selecione...</option>
              {activeTenants.map(t => (
                <option key={t.id} value={t.id}>
                  {t.organizationDisplayName || t.displayName} ({t.primaryDomain})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Tenant destino *</label>
            <select
              className="input"
              value={destinationTenantId}
              onChange={(e) => setDestinationTenantId(e.target.value)}
              required
            >
              <option value="">Selecione...</option>
              {activeTenants.filter(t => t.id !== sourceTenantId).map(t => (
                <option key={t.id} value={t.id}>
                  {t.organizationDisplayName || t.displayName} ({t.primaryDomain})
                </option>
              ))}
            </select>
          </div>
        </div>

        {sourceTenantId && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="label !mb-0">Domínios a migrar *</label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="text-xs text-slate-500 hover:text-slate-800"
                  onClick={() => setRefreshKey(k => k + 1)}
                  disabled={sourceDomainsQuery.isFetching}
                >
                  <RefreshCw className={`h-3 w-3 inline mr-1 ${sourceDomainsQuery.isFetching ? 'animate-spin' : ''}`} />
                  Atualizar
                </button>
                {sourceDomainsQuery.data && sourceDomainsQuery.data.domains.length > 1 && (
                  <button type="button" className="text-xs text-slate-500 hover:text-slate-800" onClick={selectAll}>
                    Selecionar todos
                  </button>
                )}
              </div>
            </div>

            {sourceDomainsQuery.isLoading && (
              <div className="border rounded-md p-4 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                Carregando domínios...
              </div>
            )}

            {sourceDomainsQuery.isError && (
              <div className="border border-red-200 rounded-md p-3 text-sm text-red-700 bg-red-50">
                <AlertTriangle className="h-4 w-4 inline mr-1" />
                Não foi possível carregar domínios deste tenant
              </div>
            )}

            {sourceDomainsQuery.data && (
              <div className="border rounded-md max-h-72 overflow-y-auto divide-y">
                {sourceDomainsQuery.data.domains.length === 0 && (
                  <div className="p-4 text-sm text-slate-500">Nenhum domínio encontrado</div>
                )}
                {sourceDomainsQuery.data.domains.map(d => (
                  <label
                    key={d.name}
                    className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedDomains.includes(d.name)}
                      onChange={() => toggleDomain(d.name)}
                      disabled={d.userCount === 0}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{d.name}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-2">
                        {d.isDefault && <span className="badge bg-blue-100 text-blue-800">Padrão</span>}
                        {d.isVerified && <CheckCircle2 className="h-3 w-3 text-emerald-600" />}
                        <span>{d.userCount} usuários</span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}

            <p className="text-xs text-slate-500 mt-2">
              {selectedDomains.length} domínio(s) selecionado(s)
              {sourceDomainsQuery.data && (
                <> • {sourceDomainsQuery.data.totalUsers} usuários totais no tenant</>
              )}
            </p>
          </div>
        )}

        <div className="rounded-md bg-blue-50 border border-blue-200 p-3 flex gap-2">
          <Info className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
          <div className="text-xs text-blue-800">
            <strong>Próximos passos:</strong> ao criar o projeto, o Discovery profundo vai coletar
            os usuários dos domínios selecionados, tamanhos de mailbox/OneDrive, e licenças disponíveis no destino.
            Depois disso você abre a tela de Planejamento para mapear cada usuário (UPN destino, licença, conteúdo) e gerar as ondas de migração.
          </div>
        </div>

        {create.isError && (
          <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">
            {(create.error as any)?.message || 'Erro ao criar projeto'}
          </div>
        )}

        <div className="flex gap-3">
          <button type="submit" className="btn-primary" disabled={create.isPending || selectedDomains.length === 0}>
            {create.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Criar projeto
          </button>
          <Link to="/projects" className="btn-secondary">Cancelar</Link>
        </div>
      </form>
    </div>
  )
}
